import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  ChevronLeft, ChevronRight, Link2, ShieldCheck, Smartphone, X,
} from 'lucide-react';

import { loadMobileAccounts, loadMobilePlace, loadMobileView } from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, WorkspaceTabs,
} from './parts';
import { tx } from './i18n';
import { useLiveResource } from './useLive';
import { countryName } from './countries';
import { sessionVisits } from './AppPhone';
import { ScreenIcon } from './SessionPlayer';
import { useDebounced } from './MobileAccounts';
import {
  CHART, ChartTip, DECIMAL, Flag, HourHeatmap, KpiRow, NUMBER, Panel, PlatformMark, PulseMap, RangeTabs, Share,
  ago, averageOf, counted, hourly, initials, lengthOf, percent, pointLabel, span, tipPoint, useNow, versusAverage,
} from './MobileParts';

const VERSION_PAGE = 25;

// The app from the outside, for anyone in the company: who has it open right
// now, where people are, how many open it, and on which phones and versions.
// Everything here counts people. How many requests their phones made is only
// in the Admin+ service view at the bottom.

// People with the app open in the last five minutes who have not put it away since.
function InAppNow({ people, now }) {
  const on = people.length > 0;
  return (
    <section className="xo-mob-now">
      <header>
        <span className={`xo-mob-now-dot${on ? ' is-on' : ''}`} aria-hidden="true" />
        <span className="xo-mob-now-title">
          <strong>{tx('ui.mobile.in_app_title')}</strong>
        </span>
        <strong className="xo-mob-now-count">{NUMBER.format(people.length)}</strong>
      </header>
      {on ? (
        <ol className="xo-mob-now-list">
          {people.map((person) => {
            const body = (
              <>
                <span className="xo-mob-avatar is-small">{initials(person.name, person.email)}</span>
                <span className="xo-mob-now-who">
                  <strong>{person.name || person.email || tx('ui.mobile.deleted_account')}</strong>
                  <small>
                    <PlatformMark platform={person.platform} />
                    {person.country && <><Flag code={person.country} />{person.city || countryName(person.country)}</>}
                  </small>
                </span>
                <span className="xo-mob-now-when">{tx('ui.mobile.in_app_for', { length: lengthOf(Math.max(0, now - person.since)) })}</span>
                {person.accountUuid && <ChevronRight aria-hidden="true" />}
              </>
            );
            return (
              <li key={`${person.accountUuid}-${person.at}`}>
                {person.accountUuid ? <Link to={`/xomics/mobile/accounts?account=${person.accountUuid}`}>{body}</Link> : <span>{body}</span>}
              </li>
            );
          })}
        </ol>
      ) : <p className="xo-mob-now-empty">{tx('ui.mobile.in_app_nobody')}</p>}
    </section>
  );
}

// Where people use the app from, by city or by country. Each place opens who
// used it from there and how that changed (PlacePeople).
function Places({ cities, countries, now, onOpen }) {
  const [by, setBy] = useState('city');
  const rows = by === 'city'
    ? [...cities].sort((a, b) => b.accounts - a.accounts || b.lastSeenAt - a.lastSeenAt).map((city) => ({
      key: `${city.city}-${city.country}`, code: city.country, name: city.city, people: city.accounts,
      detail: `${countryName(city.country)} · ${ago(city.lastSeenAt, now)}`, place: { country: city.country, city: city.city },
    }))
    : [...countries].sort((a, b) => b.accounts - a.accounts || b.requests - a.requests).map((country) => ({
      key: country.code, code: country.code, name: countryName(country.code), people: country.accounts,
      detail: counted('ui.mobile.cities_count', country.cities), place: { country: country.code, city: null },
    }));
  // Places only requests came from, with nobody signed in, are left out.
  const shown = rows.filter((row) => row.people > 0).slice(0, 5);
  const max = Math.max(1, ...shown.map((row) => row.people));
  return (
    <Panel title={tx('ui.mobile.places_title')} detail={tx(by === 'city' ? 'ui.mobile.places_detail' : 'ui.mobile.places_countries_detail')}>
      <WorkspaceTabs value={by} onChange={setBy} label={tx('ui.mobile.places_title')} className="is-compact xo-mob-places-tabs" tabs={[
        { value: 'city', label: tx('ui.mobile.places_cities') },
        { value: 'country', label: tx('ui.mobile.places_countries') },
      ]} />
      {shown.length === 0 ? <Empty title={tx('ui.mobile.no_places')} /> : (
        <ol className="xo-mob-places is-open">
          {shown.map((row) => (
            <li key={row.key}>
              <button type="button" onClick={() => onOpen(row.place)} title={tx('ui.mobile.place_open')}>
                <Flag code={row.code} />
                <span><strong>{row.name}</strong><small>{row.detail}</small></span>
                <Share value={row.people} total={max} />
                <em>{counted('ui.mobile.people_count', row.people)}</em>
                <ChevronRight aria-hidden="true" />
              </button>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

// Each day: the people who opened the app, and the sessions they had.
function OpeningChart({ series, step }) {
  const data = useMemo(() => {
    const first = series.findIndex((point) => point.people > 0 || point.sessions > 0);
    return first <= 0 ? series : series.slice(Math.max(0, first - 1));
  }, [series]);
  const averagePeople = useMemo(() => averageOf(data.map((point) => point.people)), [data]);
  if (data.every((point) => point.people === 0 && point.sessions === 0)) return <Empty title={tx('ui.mobile.no_activity')} />;
  const label = pointLabel(step);
  return (
    <div className="xo-mob-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={label} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={32} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
            <ChartTip title={tipPoint(step)}
              rows={(point) => [
                { name: tx('ui.mobile.people'), color: CHART.ink, mark: 'line', text: NUMBER.format(point.people) },
                { name: tx('ui.mobile.sessions'), color: CHART.pale, mark: 'bar', text: NUMBER.format(point.sessions) },
              ]}
              facts={(point) => [
                point.people > 0 ? { label: tx('ui.mobile.tip_sessions_per_person'), value: DECIMAL.format(point.sessions / point.people) } : null,
                versusAverage(point.people, averagePeople),
              ]} />
          )} />
          <Bar dataKey="sessions" fill={CHART.pale} radius={[3, 3, 0, 0]} maxBarSize={22} name={tx('ui.mobile.sessions')} />
          <Line type="monotone" dataKey="people" stroke={CHART.ink} strokeWidth={2.2} dot={false} name={tx('ui.mobile.people')} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="xo-mob-key">
        <span><i className="is-line" />{tx('ui.mobile.people')}</span>
        <span><i style={{ background: CHART.pale }} />{tx('ui.mobile.sessions')}</span>
      </div>
    </div>
  );
}

function LatestSessions({ sessions, now }) {
  if (sessions.length === 0) return <Empty title={tx('ui.mobile.no_sessions')} />;
  return (
    <ol className="xo-mob-latest">
      {sessions.slice(0, 5).map((session) => {
        const visits = sessionVisits(session).filter((visit) => visit.screen !== 'launch');
        return (
          <li key={session.id}>
            <Link to={`/xomics/mobile/usage?session=${encodeURIComponent(session.id)}`}>
              <span className="xo-mob-avatar is-small">{initials(session.name, session.email)}</span>
              <span className="xo-mob-latest-main">
                <strong>{session.name || session.email || tx('ui.mobile.deleted_account')}</strong>
                <small><PlatformMark platform={session.platform} />{lengthOf(session.end - session.start)} · {counted('ui.mobile.screens_count', visits.length)}</small>
              </span>
              <span className="xo-mob-latest-strip" aria-hidden="true">{visits.slice(0, 4).map((visit, index) => <ScreenIcon key={index} screen={visit.screen} />)}</span>
              <span className="xo-mob-latest-when">{ago(session.end, now)}</span>
              <ChevronRight aria-hidden="true" />
            </Link>
          </li>
        );
      })}
    </ol>
  );
}

function Phones({ platforms }) {
  const people = (platform) => {
    const row = platforms.people.find((entry) => entry.platform === platform);
    return row ? row.people : 0;
  };
  const reachable = (platform) => {
    const row = platforms.pushTokens.find((entry) => entry.platform === platform);
    return row ? row.accounts : 0;
  };
  const total = people('ios') + people('android');
  const methods = new Map();
  for (const row of platforms.authMethods) methods.set(row.method, (methods.has(row.method) ? methods.get(row.method) : 0) + row.accounts);
  return (
    <div className="xo-mob-platforms">
      <div className="xo-mob-duel">
        {['ios', 'android'].map((platform) => (
          <div key={platform} className={`xo-mob-duel-side is-${platform}`}>
            <PlatformMark platform={platform} withLabel />
            <strong>{NUMBER.format(people(platform))}</strong>
            <small>{percent(people(platform), total)}</small>
          </div>
        ))}
      </div>
      <div className="xo-mob-split">
        <span className="xo-mob-split-label">{tx('ui.mobile.can_get_notifications')}</span>
        <span className="xo-mob-split-bar">
          <i className="is-ios" style={{ flexGrow: reachable('ios') }}>{reachable('ios') > 0 && NUMBER.format(reachable('ios'))}</i>
          <i className="is-android" style={{ flexGrow: reachable('android') }}>{reachable('android') > 0 && NUMBER.format(reachable('android'))}</i>
        </span>
      </div>
      <div className="xo-mob-methods">
        <span className="xo-mob-split-label">{tx('ui.mobile.sign_in_methods')}</span>
        <ul>{[...methods.entries()].sort((a, b) => b[1] - a[1]).map(([method, count]) => (
          <li key={method}>{tx(`ui.mobile.method_${method}`)}<b>{NUMBER.format(count)}</b></li>
        ))}</ul>
      </div>
    </div>
  );
}

// A version line's name: the version, or one line for every build before 5.4.
function versionName(version) {
  return version === 'earlier' ? tx('ui.mobile.versions_earlier') : tx('ui.mobile.version_value', { version });
}

// The version each person used last; everything before 5.4 is one line. Each
// line opens the people on it.
function Versions({ versions, onOpen }) {
  if (versions.length === 0) return <Empty title={tx('ui.mobile.no_builds')} />;
  const total = versions.reduce((sum, entry) => sum + entry.people, 0);
  return (
    <ol className="xo-mob-versions">
      {versions.map((entry) => (
        <li key={entry.version} className={entry.version === 'earlier' ? 'is-earlier' : ''}>
          <button type="button" onClick={() => onOpen(entry.version)} title={tx('ui.mobile.version_people_open')}>
            <span className="xo-mob-version-name">
              <strong>{versionName(entry.version)}</strong>
              <small>
                {['ios', 'android'].filter((platform) => entry.platforms[platform] > 0).map((platform) => (
                  <span key={platform}><PlatformMark platform={platform} />{NUMBER.format(entry.platforms[platform])}</span>
                ))}
              </small>
            </span>
            <Share value={entry.people} total={total} />
            <span className="xo-mob-version-count">
              <strong>{counted('ui.mobile.people_count', entry.people)}</strong>
              <small>{percent(entry.people, total)}</small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button>
        </li>
      ))}
    </ol>
  );
}

// Who is on one version: the people its line counts, most recently seen first,
// each with the phone they are on it with. It is the accounts directory asked for
// that version over the page's range, so it shows names and patients to exactly
// whom the directory does. When someone moved to a version is not recorded, so
// the list says so and shows when each person was last seen.
function VersionPeople({ entry, days, now, onClose }) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const q = useDebounced(search.trim(), 280);
  useEffect(() => { setOffset(0); }, [q]);
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  const load = useCallback(() => loadMobileAccounts({
    version: entry.version, days, q, sort: 'recent', limit: VERSION_PAGE, offset,
  }), [entry.version, days, q, offset]);
  const list = useLiveResource(['mobile'], load, { reconcileMs: 60000 });
  const data = list.data;
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide xo-mob-version-people" role="dialog" aria-modal="true" aria-labelledby="xo-mob-version-people-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Smartphone aria-hidden="true" /></span>
          <div>
            <h2 id="xo-mob-version-people-title">{versionName(entry.version)}</h2>
            <p>{counted(entry.version === 'earlier' ? 'ui.mobile.version_people_earlier_detail' : 'ui.mobile.version_people_detail', entry.people)}</p>
          </div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.mobile.close')} onClick={onClose}><X /></button>
        </header>
        <div className="xo-mob-recipient-facts">
          <span><small>{tx('ui.mobile.people')}</small><strong>{NUMBER.format(entry.people)}</strong></span>
          {['ios', 'android'].map((platform) => (
            <span key={platform}>
              <small><PlatformMark platform={platform} withLabel /></small>
              <strong>{NUMBER.format(entry.platforms[platform] || 0)}</strong>
            </span>
          ))}
        </div>
        <SearchField value={search} onChange={setSearch} placeholder={tx('ui.mobile.search_people')} autoComplete="off" />
        {list.loading && data === null && <Loading label={tx('ui.mobile.reading_accounts')} />}
        {list.error && data === null && <Failed message={list.error} onRetry={list.refresh} />}
        {data && data.accounts.length === 0 && <Empty title={tx('ui.mobile.no_accounts_match')} />}
        {data && data.accounts.length > 0 && (
          <ol className="xo-mob-recipient-list xo-mob-version-list">
            {data.accounts.map((account) => (
              <li key={account.uuid}>
                <Link to={`/xomics/mobile/accounts?account=${account.uuid}`}>
                  <span className="xo-mob-avatar is-small">{initials(account.name, account.email)}</span>
                  <span className="xo-mob-recipient-main">
                    <strong>{account.name || account.email || tx('ui.mobile.no_name_yet')}</strong>
                    <small>{account.name ? account.email : ''}</small>
                  </span>
                  <span className="xo-mob-version-phone">
                    <PlatformMark platform={account.phone.platform} />
                    {account.phone.appVersion && <small>{account.phone.appVersion}</small>}
                  </span>
                  <span className="xo-mob-version-patient">
                    {account.patient === null ? <small className="is-muted">{tx('ui.mobile.not_linked')}</small> : (
                      <><Link2 aria-hidden="true" /><small>{account.patient.name || `#${account.patient.id}`}</small></>
                    )}
                  </span>
                  <span className="xo-mob-recipient-count">
                    <strong>{ago(account.lastSeenAt === null ? account.lastLoginAt : account.lastSeenAt, now)}</strong>
                    <small>{tx('ui.mobile.last_seen')}</small>
                  </span>
                  <ChevronRight aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ol>
        )}
        {data && data.total > VERSION_PAGE && (
          <div className="xo-mob-pager">
            <span>{tx('ui.mobile.showing_range', { from: NUMBER.format(offset + 1), to: NUMBER.format(offset + data.accounts.length), total: NUMBER.format(data.total) })}</span>
            <button type="button" className="xo-mob-icon-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - VERSION_PAGE))}
              aria-label={tx('ui.mobile.previous_page')}><ChevronLeft /></button>
            <button type="button" className="xo-mob-icon-button" disabled={offset + VERSION_PAGE >= data.total} onClick={() => setOffset(offset + VERSION_PAGE)}
              aria-label={tx('ui.mobile.next_page')}><ChevronRight /></button>
          </div>
        )}
        <p className="xo-mob-footnote">{tx('ui.mobile.version_since_unrecorded')}</p>
      </motion.div>
    </div>
  );
}

// Who used the app from one place over the page's range, counted as Top places
// counts them, and how that changed: people and sessions from there per day,
// then each person, most recently seen there first, with their sessions there.
// `cities`: the overview's cities, each with its coordinates and people, for the map.
function PlacePeople({ place, cities, days, now, onClose }) {
  const [search, setSearch] = useState('');
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  const load = useCallback(() => loadMobilePlace({ days, country: place.country, city: place.city }), [days, place.country, place.city]);
  const report = useLiveResource(['mobile'], load, { reconcileMs: 60000 });
  const data = report.data;
  const term = search.trim().toLowerCase();
  const people = data === null ? [] : data.people.filter((person) => !term
    || [person.name, person.email, ...person.cities].some((value) => typeof value === 'string' && value.toLowerCase().includes(term)));
  const onPlatform = (platform) => (data === null ? 0 : data.people.filter((person) => person.platform === platform).length);
  // The country's cities as dots sized by their people, framed on the map; the
  // time of each is not what the map is for, so none pulses.
  const mapCities = cities.filter((city) => city.country === place.country && city.lat && city.lng)
    .map((city) => ({ city: city.city, country: city.country, lat: city.lat, lng: city.lng, at: 0, count: city.accounts }));
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide xo-mob-place-people" role="dialog" aria-modal="true" aria-labelledby="xo-mob-place-people-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Flag code={place.country} /></span>
          <div>
            <h2 id="xo-mob-place-people-title">{place.city || countryName(place.country)}</h2>
            <p>{[place.city ? countryName(place.country) : null, data === null ? null : counted('ui.mobile.place_detail', data.people.length)].filter(Boolean).join(' · ')}</p>
          </div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.mobile.close')} onClick={onClose}><X /></button>
        </header>
        {report.loading && data === null && <Loading label={tx('ui.mobile.reading_place')} />}
        {report.error && data === null && <Failed message={report.error} onRetry={report.refresh} />}
        {data !== null && (
          <div className="xo-mob-place-body">
            <div className="xo-mob-place-main">
              <div className="xo-mob-recipient-facts">
                <span><small>{tx('ui.mobile.people')}</small><strong>{NUMBER.format(data.people.length)}</strong></span>
                <span><small>{tx('ui.mobile.sessions')}</small><strong>{NUMBER.format(data.series.reduce((sum, point) => sum + point.sessions, 0))}</strong></span>
                {['ios', 'android'].map((platform) => (
                  <span key={platform}><small><PlatformMark platform={platform} withLabel /></small><strong>{NUMBER.format(onPlatform(platform))}</strong></span>
                ))}
              </div>
              <div className="xo-mob-place-map">
                <PulseMap countries={[{ code: place.country, accounts: data.people.length }]} pings={mapCities} fit={mapCities.length > 0}
                  pingCountKey="ui.mobile.people_count" height={300} />
              </div>
              <div className="xo-mob-place-chart"><OpeningChart series={data.series} step={data.step} /></div>
            </div>
            <div className="xo-mob-place-side">
            <SearchField value={search} onChange={setSearch} placeholder={tx('ui.mobile.search_people')} autoComplete="off" />
            {people.length === 0 ? <Empty title={tx('ui.mobile.no_accounts_match')} /> : (
              <ol className="xo-mob-recipient-list">
                {people.map((person, index) => {
                  const body = (
                    <>
                      <span className="xo-mob-avatar is-small">{initials(person.name, person.email)}</span>
                      <span className="xo-mob-recipient-main">
                        <strong>{person.accountUuid === null ? tx('ui.mobile.deleted_account') : (person.name || person.email || tx('ui.mobile.no_name_yet'))}</strong>
                        <small>{person.name ? person.email : ''}</small>
                      </span>
                      <span className="xo-mob-version-phone">
                        {person.platform && <PlatformMark platform={person.platform} />}
                        {place.city === null && <small>{person.cities.join(', ')}</small>}
                      </span>
                      <span className="xo-mob-recipient-count">
                        <strong>{counted('ui.mobile.sessions_count', person.sessions)}</strong>
                        <small>{ago(person.firstSeenAt, now)}</small>
                      </span>
                      <span className="xo-mob-recipient-count">
                        <strong>{ago(person.lastSeenAt, now)}</strong>
                        <small>{tx('ui.mobile.last_seen')}</small>
                      </span>
                    </>
                  );
                  return (
                    <li key={person.accountUuid || `gone-${index}`}>
                      {person.accountUuid === null ? <span>{body}<i /></span> : (
                        <Link to={`/xomics/mobile/accounts?account=${person.accountUuid}`}>{body}<ChevronRight aria-hidden="true" /></Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// Admin+ only, and folded away: how fast the app's backend answers and where it errs.
function ServiceHealth({ data }) {
  const slowest = Math.max(1, ...data.api.routes.map((route) => route.avgMs));
  return (
    <details className="xo-mob-diagnostics">
      <summary>
        <ShieldCheck aria-hidden="true" />
        <span><strong>{tx('ui.mobile.service_health')}</strong><small>{tx('ui.mobile.service_health_detail')}</small></span>
        <Pill tone="flat">{tx('ui.mobile.admin_plus_only')}</Pill>
      </summary>
      <div className="xo-mob-diagnostics-body">
        <div className="xo-mob-diag-facts">
          <span><small>{tx('ui.mobile.api_latency')}</small><strong>{span(data.api.avgMs)}</strong><em>{tx('ui.mobile.slowest_value', { value: span(data.api.maxMs) })}</em></span>
          <span><small>{tx('ui.mobile.server_errors')}</small><strong>{NUMBER.format(data.api.serverErrors)}</strong><em>{counted('ui.mobile.client_errors', data.api.clientErrors)}</em></span>
          <span><small>{tx('ui.mobile.error_days')}</small><strong>{NUMBER.format(data.activity.series.filter((point) => point.failures > 0).length)}</strong><em>{tx('ui.mobile.error_days_detail')}</em></span>
        </div>
        <h3 className="xo-mob-subhead">{tx('ui.mobile.api_title')}</h3>
        <ol className="xo-mob-route-list">
          {data.api.routes.map((route) => (
            <li key={route.route}>
              <code>{route.route}</code>
              <span><i style={{ width: `${(route.avgMs / slowest) * 100}%` }} /></span>
              <em>{span(route.avgMs)}</em>
              <small>{counted('ui.mobile.requests_count', route.requests)} · {counted('ui.mobile.errors_count', route.errors)}</small>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

export default function MobileOverview() {
  const [days, setDays] = useState(30);
  const load = useCallback(() => loadMobileView('overview', days), [days]);
  const report = useLiveResource([], load, { reconcileMs: 60000 });
  const now = useNow(30000);
  const [openVersion, setOpenVersion] = useState(null);
  const closeVersion = useCallback(() => setOpenVersion(null), []);
  const [openPlace, setOpenPlace] = useState(null);
  const closePlace = useCallback(() => setOpenPlace(null), []);

  if (report.loading && report.data === null) return <Loading label={tx('ui.mobile.reading_pulse')} />;
  if (report.error && report.data === null) return <Failed message={report.error} onRetry={report.refresh} />;
  const data = report.data;
  const series = data.sessions.series;
  // Read from the latest answer, so its counts stay live while it is open.
  const openEntry = openVersion === null ? null : data.platforms.versions.find((entry) => entry.version === openVersion) || null;

  return (
    <div className="xo-platform-page xo-mobile-page">
      <Head title={tx('ui.mobile.pulse_title')} subtitle={tx('ui.mobile.pulse_subtitle')}>
        <RangeTabs value={days} onChange={setDays} />
      </Head>
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}

      <div className="xo-mob-hero">
        <Panel title={tx('ui.mobile.map_title')} detail={tx('ui.mobile.map_detail')} className="xo-mob-hero-map">
          <PulseMap countries={data.geography.countries} pings={data.pings} height={440} />
        </Panel>
        <div className="xo-mob-hero-side">
          <InAppNow people={data.activity.inApp.people} now={now} />
          <Places cities={data.geography.cities} countries={data.geography.countries} now={now} onOpen={setOpenPlace} />
        </div>
      </div>

      <KpiRow items={[
        { label: tx('ui.mobile.opened_label'), value: NUMBER.format(data.activity.opened.day),
          detail: tx('ui.mobile.opened_detail', { week: NUMBER.format(data.activity.opened.week) }), trend: series.map((point) => point.people) },
        { label: tx('ui.mobile.sessions'), value: NUMBER.format(data.sessions.total),
          detail: tx('ui.mobile.typical_session', { length: lengthOf(data.sessions.medianMs) }), trend: series.map((point) => point.sessions) },
        { label: tx('ui.mobile.new_app_label'), value: NUMBER.format(data.accounts.signedIn),
          detail: tx('ui.mobile.new_app_detail', { total: NUMBER.format(data.accounts.total) }) },
        { label: tx('ui.mobile.new_accounts_label'), value: NUMBER.format(data.accounts.joined),
          detail: tx('ui.mobile.new_accounts_detail'), trend: data.accounts.joinedSeries.map((point) => point.joined) },
      ]} />

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={tx('ui.mobile.activity_title')} detail={tx(hourly(data.step) ? 'ui.mobile.activity_detail_hours' : 'ui.mobile.activity_detail')}>
          <OpeningChart series={series} step={data.step} />
        </Panel>
        <Panel title={tx('ui.mobile.latest_sessions')} detail={tx('ui.mobile.latest_sessions_detail')}
          aside={<Link className="xo-mob-link" to="/xomics/mobile/usage">{tx('ui.mobile.all_sessions')}<ChevronRight aria-hidden="true" /></Link>}>
          <LatestSessions sessions={data.sessions.latest} now={now} />
        </Panel>
      </div>

      <div className="xo-mob-row is-halves is-even">
        <Panel title={tx('ui.mobile.duel_title')} detail={tx('ui.mobile.duel_detail')}>
          <Phones platforms={data.platforms} />
        </Panel>
        <Panel title={tx('ui.mobile.builds_title')} detail={tx('ui.mobile.builds_detail')}>
          <Versions versions={data.platforms.versions} onOpen={setOpenVersion} />
        </Panel>
      </div>

      <Panel title={tx('ui.mobile.when_title')} detail={tx('ui.mobile.when_detail')}>
        <HourHeatmap grid={data.activity.heatmap} countKey="ui.mobile.sessions_count" />
      </Panel>

      {data.diagnostics && <ServiceHealth data={data} />}

      <AnimatePresence>
        {openEntry && <VersionPeople key={openEntry.version} entry={openEntry} days={days} now={now} onClose={closeVersion} />}
        {openPlace && <PlacePeople key={`${openPlace.country}-${openPlace.city}`} place={openPlace} cities={data.geography.cities} days={days} now={now} onClose={closePlace} />}
      </AnimatePresence>
    </div>
  );
}
